/**
 * The audio companion, once for the whole tab (plan §9.11).
 *
 * The Rotom Phone is a shell of apps, and the music must not notice which one is open: the engine, its
 * socket and the service worker's warming live HERE, above every route, so navigating from Music to Stats
 * to the Map re-renders apps but never rebuilds the engine, closes the socket or restarts a track, a
 * crossfade or a shared instance. Apps read it with {@link useAudioCompanion}; the status bar and the
 * settings read it without switching it on, with {@link useAudioCompanionState}.
 */
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

import { useCompanionMachine, type CompanionOptions } from './useAudioCompanion';

export type AudioCompanion = ReturnType<typeof useCompanionMachine> & {
  /** Whether an app has switched the companion on in this tab (it then stays on). */
  readonly active: boolean;
  readonly activate: () => void;
};

const CompanionContext = createContext<AudioCompanion | null>(null);

export function AudioCompanionProvider({ backendFactory, children }: CompanionOptions & { readonly children: ReactNode }): React.JSX.Element {
  const [active, setActive] = useState(false);
  const machine = useCompanionMachine({ ...(backendFactory === undefined ? {} : { backendFactory }), active });
  const activate = useCallback(() => setActive(true), []);
  return <CompanionContext.Provider value={{ ...machine, active, activate }}>{children}</CompanionContext.Provider>;
}

/**
 * The companion, switched on: the Music app, the home screen and the map ask for it — and from then on it
 * runs for the rest of the tab's life, whichever app is open.
 */
export function useAudioCompanion(): AudioCompanion {
  const companion = useAudioCompanionState();
  const { activate } = companion;
  useEffect(() => activate(), [activate]);
  return companion;
}

/** The companion as it is, without switching it on. */
export function useAudioCompanionState(): AudioCompanion {
  const companion = useContext(CompanionContext);
  if (companion === null) throw new Error('useAudioCompanion must be used inside <AudioCompanionProvider>');
  return companion;
}
